/**
 * Parse and format cron expression to human-readable format
 */
export const parseCronExpression = (cronExpression: string): string => {
  const parts = cronExpression.trim().split(/\s+/);

  if (parts.length !== 5) {
    return cronExpression; // Return original if invalid format
  }

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  const descriptions: string[] = [];

  // Parse hour and minute
  if (hour === '*' && minute === '*') {
    descriptions.push('Every minute');
  } else if (hour !== '*' && minute !== '*') {
    descriptions.push(`At ${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`);
  } else if (hour !== '*') {
    descriptions.push(`Every minute during hour ${hour}`);
  } else if (minute !== '*') {
    descriptions.push(`At minute ${minute} of every hour`);
  }

  // Parse day of week
  if (dayOfWeek !== '*') {
    const dayNames = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];
    if (dayOfWeek.includes('-')) {
      const [start, end] = dayOfWeek.split('-').map(d => parseInt(d));
      descriptions.push(`on ${dayNames[start]} through ${dayNames[end]}`);
    } else if (dayOfWeek.includes(',')) {
      const days = dayOfWeek.split(',').map(d => dayNames[parseInt(d)]);
      descriptions.push(`on ${days.join(', ')}`);
    } else if (dayOfWeek === '1-5') {
      descriptions.push('on weekdays');
    } else {
      descriptions.push(`on ${dayNames[parseInt(dayOfWeek)]}`);
    }
  }

  // Parse day of month
  if (dayOfMonth !== '*' && dayOfWeek === '*') {
    if (dayOfMonth.includes(',')) {
      descriptions.push(`on day ${dayOfMonth} of the month`);
    } else {
      descriptions.push(`on day ${dayOfMonth} of the month`);
    }
  }

  // Parse month
  if (month !== '*') {
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    if (month.includes(',')) {
      const months = month.split(',').map(m => monthNames[parseInt(m) - 1]);
      descriptions.push(`in ${months.join(', ')}`);
    } else if (month.includes('-')) {
      const [start, end] = month.split('-').map(m => parseInt(m));
      descriptions.push(
        `from ${monthNames[start - 1]} to ${monthNames[end - 1]}`,
      );
    } else {
      descriptions.push(`in ${monthNames[parseInt(month) - 1]}`);
    }
  }

  return descriptions.join(' ') || cronExpression;
};

/**
 * Validate cron expression format
 */
export const validateCronExpression = (
  cronExpression: string,
): { valid: boolean; error?: string } => {
  const parts = cronExpression.trim().split(/\s+/);

  if (parts.length !== 5) {
    return {
      valid: false,
      error:
        'Cron expression must have exactly 5 fields (minute hour day month weekday)',
    };
  }

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  // Validate minute (0-59)
  if (!isValidCronField(minute, 0, 59)) {
    return { valid: false, error: 'Invalid minute field (0-59)' };
  }

  // Validate hour (0-23)
  if (!isValidCronField(hour, 0, 23)) {
    return { valid: false, error: 'Invalid hour field (0-23)' };
  }

  // Validate day of month (1-31)
  if (!isValidCronField(dayOfMonth, 1, 31)) {
    return { valid: false, error: 'Invalid day of month field (1-31)' };
  }

  // Validate month (1-12)
  if (!isValidCronField(month, 1, 12)) {
    return { valid: false, error: 'Invalid month field (1-12)' };
  }

  // Validate day of week (0-6)
  if (!isValidCronField(dayOfWeek, 0, 6)) {
    return { valid: false, error: 'Invalid day of week field (0-6)' };
  }

  return { valid: true };
};

/**
 * Helper function to validate a cron field
 */
const isValidCronField = (field: string, min: number, max: number): boolean => {
  if (field === '*') return true;

  // Handle ranges (e.g., 1-5)
  if (field.includes('-')) {
    const [start, end] = field.split('-').map(n => parseInt(n));
    return (
      !isNaN(start) && !isNaN(end) && start >= min && end <= max && start <= end
    );
  }

  // Handle lists (e.g., 1,3,5)
  if (field.includes(',')) {
    const numbers = field.split(',').map(n => parseInt(n));
    return numbers.every(n => !isNaN(n) && n >= min && n <= max);
  }

  // Handle step values (e.g., */5)
  if (field.includes('/')) {
    const [range, step] = field.split('/');
    const stepNum = parseInt(step);
    if (isNaN(stepNum) || stepNum <= 0) return false;
    if (range === '*') return true;
    return isValidCronField(range, min, max);
  }

  // Handle single number
  const num = parseInt(field);
  return !isNaN(num) && num >= min && num <= max;
};

/**
 * Get common cron expression templates
 */
export const getCronTemplates = () => [
  {
    label: 'Every weekday at 9:00 AM',
    value: '0 9 * * 1-5',
    description: 'Start work hours',
  },
  {
    label: 'Every weekday at 6:00 PM',
    value: '0 18 * * 1-5',
    description: 'End work hours',
  },
  {
    label: 'Every day at 8:00 AM',
    value: '0 8 * * *',
    description: 'Daily morning',
  },
  {
    label: 'Every day at 10:00 PM',
    value: '0 22 * * *',
    description: 'Daily night',
  },
  {
    label: 'Every Monday at 9:00 AM',
    value: '0 9 * * 1',
    description: 'Weekly start',
  },
  {
    label: 'Every Friday at 5:00 PM',
    value: '0 17 * * 5',
    description: 'Weekly end',
  },
  {
    label: 'Every Saturday at 12:00 AM',
    value: '0 0 * * 6',
    description: 'Weekend start',
  },
  {
    label: 'Every Sunday at 11:59 PM',
    value: '59 23 * * 0',
    description: 'Weekend end',
  },
];

/**
 * Format cron expression with colored segments
 */
export const formatCronParts = (cronExpression: string) => {
  const parts = cronExpression.trim().split(/\s+/);

  if (parts.length !== 5) {
    return null;
  }

  return {
    minute: parts[0],
    hour: parts[1],
    dayOfMonth: parts[2],
    month: parts[3],
    dayOfWeek: parts[4],
  };
};
